import React, { useState } from "react";
import { X, Plus, Trash2, Building2 } from "lucide-react";
import { toast } from "sonner";
import { createClient } from "../../api";

const FORMS = [
  { value: "1065", label: "Partnership (Form 1065)" },
  { value: "1120S", label: "S-Corporation (Form 1120-S)" },
  { value: "1120", label: "C-Corporation (Form 1120)" },
  { value: "1040", label: "Sole Prop / Schedule C (Form 1040)" },
];

const inputCls =
  "w-full bg-zinc-900 border border-white/20 rounded-md px-2.5 py-1.5 text-xs text-zinc-300 focus:outline-none focus:border-blue-500";

export const AddClientModal = ({ open, onClose, coaOptions, onCreated }) => {
  const [name, setName] = useState("");
  const [form, setForm] = useState("1065");
  const [ein, setEin] = useState("");
  const [naics, setNaics] = useState("");
  const [headcount, setHeadcount] = useState("1");
  const [accounts, setAccounts] = useState([{ institution: "", account_type: "checking", last4: "" }]);
  const [rules, setRules] = useState([]);
  const [revenueRule, setRevenueRule] = useState(true);
  const [saving, setSaving] = useState(false);

  if (!open) return null;

  const setAcct = (i, k, v) => setAccounts((p) => p.map((a, j) => (j === i ? { ...a, [k]: v } : a)));
  const setRule = (i, k, v) => setRules((p) => p.map((r, j) => (j === i ? { ...r, [k]: v } : r)));

  const submit = async () => {
    setSaving(true);
    try {
      const res = await createClient({
        legal_name: name,
        entity_type: form,
        ein: ein || null,
        naics: naics || null,
        headcount: parseInt(headcount) || 1,
        accounts,
        rules,
        revenue_rule: revenueRule,
      });
      toast.success(`${res.name} onboarded`, {
        description: `Engagement created in ${res.state.replace(/_/g, " ")} — upload the first statement to begin.`,
      });
      onCreated(res.slug);
      onClose();
    } catch (e) {
      toast.error("Could not create client", {
        description: (e.response && e.response.data && e.response.data.detail) || "Request failed",
      });
    }
    setSaving(false);
  };

  return (
    <div
      data-testid="add-client-modal"
      className="fixed inset-0 bg-black/70 backdrop-blur-md z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="glass-panel rounded-md shadow-2xl max-w-lg w-full p-6 text-zinc-200 border border-white/10 space-y-4 bg-zinc-900/95 max-h-[90vh] overflow-y-auto custom-scrollbar"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-white/10 pb-3">
          <div className="flex items-center space-x-2">
            <Building2 className="w-5 h-5 text-blue-400" />
            <h3 className="font-bold text-sm text-white">Onboard New Client</h3>
          </div>
          <button data-testid="add-client-close" onClick={onClose} className="text-zinc-500 hover:text-zinc-200 transition">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3 text-xs">
          <div className="col-span-2">
            <label className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold">Legal name *</label>
            <input data-testid="client-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Summit Legal Group LLP" className={`${inputCls} mt-1`} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold">Entity type *</label>
            <select data-testid="client-form-select" value={form} onChange={(e) => setForm(e.target.value)} className={`${inputCls} mt-1 cursor-pointer`}>
              {FORMS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold">Headcount</label>
            <input data-testid="client-headcount-input" value={headcount} onChange={(e) => setHeadcount(e.target.value)} className={`${inputCls} mt-1`} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold">EIN</label>
            <input data-testid="client-ein-input" value={ein} onChange={(e) => setEin(e.target.value)} placeholder="00-0000000" className={`${inputCls} mt-1`} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold">NAICS</label>
            <input data-testid="client-naics-input" value={naics} onChange={(e) => setNaics(e.target.value)} placeholder="541110" className={`${inputCls} mt-1`} />
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold">Bank accounts *</span>
            <button
              data-testid="add-account-row-btn"
              onClick={() => setAccounts((p) => [...p, { institution: "", account_type: "checking", last4: "" }])}
              className="text-[11px] text-blue-400 font-semibold flex items-center hover:text-blue-400"
            >
              <Plus className="w-3 h-3 mr-0.5" /> Add account
            </button>
          </div>
          {accounts.map((a, i) => (
            <div key={i} className="flex items-center space-x-2 text-xs" data-testid={`account-row-${i}`}>
              <input value={a.institution} onChange={(e) => setAcct(i, "institution", e.target.value)} placeholder="Institution" className={inputCls} data-testid={`account-institution-${i}`} />
              <select value={a.account_type} onChange={(e) => setAcct(i, "account_type", e.target.value)} className={`${inputCls} cursor-pointer w-40`} data-testid={`account-type-${i}`}>
                <option value="checking">Checking</option>
                <option value="savings">Savings</option>
                <option value="credit_card">Credit Card</option>
              </select>
              <input value={a.last4} onChange={(e) => setAcct(i, "last4", e.target.value)} placeholder="Last 4" maxLength={4} className={`${inputCls} w-20`} data-testid={`account-last4-${i}`} />
              {accounts.length > 1 && (
                <button onClick={() => setAccounts((p) => p.filter((_, j) => j !== i))} className="text-zinc-500 hover:text-rose-400">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold">Deterministic entity rules (optional)</span>
            <button
              data-testid="add-rule-row-btn"
              onClick={() => setRules((p) => [...p, { payee_contains: "", coa_account: "6000" }])}
              className="text-[11px] text-blue-400 font-semibold flex items-center hover:text-blue-400"
            >
              <Plus className="w-3 h-3 mr-0.5" /> Add rule
            </button>
          </div>
          {rules.map((r, i) => (
            <div key={i} className="flex items-center space-x-2 text-xs" data-testid={`rule-row-${i}`}>
              <input value={r.payee_contains} onChange={(e) => setRule(i, "payee_contains", e.target.value)} placeholder="Payee contains… e.g. WESTLAW" className={inputCls} data-testid={`rule-payee-${i}`} />
              <select value={r.coa_account} onChange={(e) => setRule(i, "coa_account", e.target.value)} className={`${inputCls} cursor-pointer`} data-testid={`rule-coa-${i}`}>
                {coaOptions.map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
              </select>
              <button onClick={() => setRules((p) => p.filter((_, j) => j !== i))} className="text-zinc-500 hover:text-rose-400">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          <label className="flex items-center space-x-2 text-xs text-zinc-400 cursor-pointer">
            <input
              type="checkbox"
              data-testid="revenue-rule-checkbox"
              checked={revenueRule}
              onChange={(e) => setRevenueRule(e.target.checked)}
              className="rounded border-white/20 text-blue-400 focus:ring-blue-500"
            />
            <span>Auto-classify deposits as revenue (rule E-REV)</span>
          </label>
        </div>

        <button
          data-testid="create-client-btn"
          onClick={submit}
          disabled={saving}
          className="w-full bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white font-semibold py-2.5 rounded-md text-xs transition shadow-glow-blue"
        >
          {saving ? "Creating engagement…" : "Create Client & Engagement"}
        </button>
      </div>
    </div>
  );
};
